// src/services/chatService.ts
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChatsType, MessageType } from '../types/chat/chat';
import { FIREBASE_COLLECTIONS } from '../Component/api/url';
import {
  uploadFile,
  getUserInfo as fetchUserInfo,
  getUser,
} from './chat';

export class ChatService {
  private chatGroupsCollection = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS);
  private usersCollection      = collection(db, FIREBASE_COLLECTIONS.USERS);

  // ── File upload ─────────────────────────────────────────────────────────────

  private async uploadFileToBackend(file: File): Promise<{ url: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const data = await uploadFile(formData);
    if (data.success === 1 || data.success === '1') {
      return { url: data.file_url, fileName: data.file_name };
    }
    throw new Error(data.message || 'Upload failed');
  }

  // ── ID helpers ───────────────────────────────────────────────────────────────

  private extractUserId(fullId: string): string {
    const match = fullId.match(/\d+$/);
    return match ? match[0] : fullId;
  }

  private getUserType(fullId: string): 'user' | 'nutritionist' | 'admin' {
    if (fullId.startsWith('userid_')) return 'user';
    if (fullId.startsWith('nutritionistid_')) return 'nutritionist';
    if (fullId.startsWith('adminid_')) return 'admin';
    return 'user';
  }

  private isValidPrefixedId(userId: string): boolean {
    return (
      userId.startsWith('userid_') ||
      userId.startsWith('nutritionistid_') ||
      userId.startsWith('adminid_')
    );
  }

  private getUserDataFromLocalStorage() {
    try {
      const s = localStorage.getItem('user');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }

  // ── Ensure Firestore user doc exists ────────────────────────────────────────

  private async ensureUserExists(userId: string): Promise<void> {
    if (!this.isValidPrefixedId(userId)) return;

    try {
      const ref  = doc(this.usersCollection, userId);
      const snap = await getDoc(ref);
      if (snap.exists()) return;

      const type      = this.getUserType(userId);
      const numericId = this.extractUserId(userId);
      const local     = this.getUserDataFromLocalStorage();

      const data: any = {
        onlineStatus: 'offline',
        profile_url:
          'https://img.freepik.com/premium-photo/portrait-serious-businesswoman-using-laptop-office_118454-14967.jpg',
        user_id: numericId,
      };

      if (type === 'nutritionist' || type === 'admin') {
        data.user_fname = local?.nutrist_fname || (type === 'admin' ? 'Admin' : 'Nutritionist');
        data.user_lname = local?.nutrist_lname || numericId;
        if (local?.nutrist_profile) data.profile_url = local.nutrist_profile;
      } else {
        data.user_fname = 'User';
        data.user_lname = numericId;
      }

      await setDoc(ref, data);
      console.log('✅ Created user in Firestore:', userId);
    } catch (e) {
      console.error('❌ ensureUserExists error:', e);
    }
  }

  // ── MAIN: Subscribe to chats for the logged-in nutritionist ────────────────
  //
  // HOW THE FILTER WORKS:
  //   Firestore: where('members', 'array-contains', userId)
  //   → Only returns chat docs where the members array contains EXACTLY this userId string.
  //   → e.g. userId = "nutritionistid_8"
  //        chat A members: ["userid_11", "nutritionistid_8", "adminid_2"]  ← returned ✅
  //        chat B members: ["userid_22", "nutritionistid_5", "adminid_2"]  ← NOT returned ✅
  //        chat C members: ["userid_33", "nutritionistid_8", "nutritionistid_5"] ← returned ✅ (both see it)
  //
  // If ALL chats are showing despite this, the userId value is wrong.
  // Run ChatDebug.tsx to inspect what value is actually being used.

  subscribeToChats(userId: string, callback: (chats: ChatsType[]) => void): () => void {
    // ── GUARD: reject invalid / unprefixed IDs immediately ──────────────────
    if (!userId || !this.isValidPrefixedId(userId)) {
      console.error(
        '❌ subscribeToChats BLOCKED — userId invalid or missing prefix.',
        '\n   Got:', JSON.stringify(userId),
        '\n   Expected: "nutritionistid_8" / "adminid_2" / "userid_11"',
        '\n   Fix: call initializeFirebaseChatUser(user) right after login in AuthContext.'
      );
      callback([]);
      return () => {};
    }

    console.log('📡 subscribeToChats — userId:', userId);
    this.ensureUserExists(userId);

    // Firestore query — only chats where this userId is in the members array
    const q = query(
      this.chatGroupsCollection,
      where('members', 'array-contains', userId)
    );

    return onSnapshot(
      q,
      async (snapshot) => {
        console.log(`📋 Firestore returned ${snapshot.docs.length} chats for "${userId}"`);

        // Log each doc's members for debugging
        snapshot.docs.forEach(d => {
          console.log(`  Chat "${d.id}" members:`, d.data().members);
        });

        const resolved = await Promise.all(
          snapshot.docs.map(async (docSnap): Promise<ChatsType | null> => {
            const data = docSnap.data();

            // ── 1. Skip inactive ───────────────────────────────────────────────
            if (data.isActive === false) {
              console.log('  ⏭ Skipping inactive chat:', docSnap.id);
              return null;
            }

            // ── 2. Verify userId is truly in this doc's members ────────────────
            const members: string[] = data.members || [];
            if (!members.includes(userId)) {
              console.warn('  ⚠️ Doc returned by Firestore but userId not in members — skipping:', docSnap.id);
              return null;
            }

            // ── 3. Find the client (userid_) ───────────────────────────────────
            const clientId = members.find((m) => m.startsWith('userid_'));
            if (!clientId) {
              console.warn('  ⚠️ No userid_ in members, skipping:', docSnap.id, members);
              return null;
            }

            // ── 5. Resolve lastMessageAt from doc-level lastMessage ────────────
            const rawTs = data.lastMessage?.timestamp;
            let lastMessageAt: Date | null = null;
            if (rawTs) {
              if (typeof rawTs.toDate === 'function') lastMessageAt = rawTs.toDate();
              else if (rawTs.seconds) lastMessageAt = new Date(rawTs.seconds * 1000);
            }

            let excerptText: string = data.lastMessage?.text || '';

            const needsLastMessageFallback = !lastMessageAt || !excerptText;

            // Fire client info + (optional) last-message fallback in parallel
            const [clientInfo, lastMsgSnap] = await Promise.all([
              this.getUserInfo(clientId),
              needsLastMessageFallback
                ? getDocs(
                    query(
                      collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, docSnap.id, 'messages'),
                      orderBy('timestamp', 'desc'),
                      limit(1)
                    )
                  ).catch(() => null)
                : Promise.resolve(null),
            ]);

            if (lastMsgSnap && !lastMsgSnap.empty) {
              const m = lastMsgSnap.docs[0].data();
              if (!excerptText) {
                if (m.type === 'image') excerptText = '📷 Sent a photo';
                else if (m.type === 'file') excerptText = '📎 Sent a file';
                else excerptText = m.content || m.text || '';
              }
              if (!lastMessageAt && m.timestamp) {
                if (typeof m.timestamp.toDate === 'function') lastMessageAt = m.timestamp.toDate();
                else if (m.timestamp.seconds) lastMessageAt = new Date(m.timestamp.seconds * 1000);
              }
            }

            const status = this.determineStatus(clientInfo?.onlineStatus);
            const clientName = clientInfo
              ? `${clientInfo.user_fname || ''} ${clientInfo.user_lname || ''}`.trim()
              : 'Unknown User';

            return {
              id:            docSnap.id,
              name:          data.name || clientName || 'Unknown User',
              status,
              thumb:         data.photoURL || clientInfo?.profile_url || '/default-avatar.jpg',
              recent:        false,
              excerpt:       excerptText || 'No messages yet',
              lastMessageAt,
              messages:      [],
              clientUserId:  clientId, // e.g. "userid_123"
            };
          })
        );

        const chats: ChatsType[] = resolved.filter((c): c is ChatsType => c !== null);

        chats.sort((a, b) => {
          const at = a.lastMessageAt?.getTime() ?? 0;
          const bt = b.lastMessageAt?.getTime() ?? 0;
          return bt - at;
        });

        console.log(`✅ Resolved ${chats.length} chat(s) to display for: ${userId}`);
        callback(chats);
      },
      (err) => console.error('❌ onSnapshot error:', err)
    );
  }

  // ── Messages subscription ────────────────────────────────────────────────────

  subscribeToMessages(chatId: string, callback: (messages: MessageType[]) => void): () => void {
    const ref = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId, 'messages');
    const q   = query(ref, orderBy('timestamp', 'asc'));

    return onSnapshot(
      q,
      (snap) => {
        const messages: MessageType[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id:         d.id,
            msg:        data.content || data.text || '',
            senderId:   data.sender || '',
            type:       data.type || 'text',
            attachment: data.attachments || [],
            createdAt:  data.timestamp?.toDate() || new Date(),
            seenBy:     data.seenBy || [],
          };
        });
        callback(messages);
      },
      (e) => console.error('❌ messages snapshot error:', e)
    );
  }

  // ── Send text ────────────────────────────────────────────────────────────────

  async sendMessage(chatId: string, message: string, senderId: string) {
    await this.ensureUserExists(senderId);
    const ref    = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId, 'messages');
    const msgRef = await addDoc(ref, {
      content:   message,
      sender:    senderId,
      type:      'text',
      timestamp: serverTimestamp(),
      seenBy:    [senderId],
    });
    await updateDoc(doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId), {
      'lastMessage.text':      message,
      'lastMessage.sender':    senderId,
      'lastMessage.timestamp': serverTimestamp(),
    });
    return msgRef.id;
  }

  // ── Send image ───────────────────────────────────────────────────────────────

  async sendImageMessage(chatId: string, imageFile: File, senderId: string) {
    await this.ensureUserExists(senderId);
    const { url } = await this.uploadFileToBackend(imageFile);
    const ref     = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId, 'messages');
    const msgRef  = await addDoc(ref, {
      content:   url,
      sender:    senderId,
      type:      'image',
      timestamp: serverTimestamp(),
      seenBy:    [senderId],
    });
    await updateDoc(doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId), {
      'lastMessage.text':      'Sent a photo',
      'lastMessage.sender':    senderId,
      'lastMessage.timestamp': serverTimestamp(),
    });
    return msgRef.id;
  }

  // ── Send with attachments ────────────────────────────────────────────────────

  async sendMessageWithAttachments(chatId: string, message: string, files: File[], senderId: string) {
    await this.ensureUserExists(senderId);
    const attachments = await Promise.all(files.map((f) => this.buildAttachment(f)));
    const ref         = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId, 'messages');
    const msgRef      = await addDoc(ref, {
      content:     message || 'Sent attachments',
      sender:      senderId,
      type:        'file',
      timestamp:   serverTimestamp(),
      seenBy:      [senderId],
      attachments,
    });
    await updateDoc(doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId), {
      'lastMessage.text':      message || `Sent ${files.length} file(s)`,
      'lastMessage.sender':    senderId,
      'lastMessage.timestamp': serverTimestamp(),
    });
    return msgRef.id;
  }

  // ── Mark as read ─────────────────────────────────────────────────────────────

  async markMessagesAsRead(chatId: string, userId: string) {
    const ref  = collection(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId, 'messages');
    const q    = query(ref, where('sender', '!=', userId));
    const snap = await getDocs(q);

    let count = 0;
    await Promise.all(
      snap.docs.map(async (d) => {
        const seenBy: string[] = d.data().seenBy || [];
        if (!seenBy.includes(userId)) {
          count++;
          return updateDoc(doc(ref, d.id), { seenBy: [...seenBy, userId] });
        }
      })
    );
    if (count > 0) console.log(`✅ Marked ${count} message(s) as read`);
  }

  // ── Chat group details ───────────────────────────────────────────────────────

  async getChatGroupDetails(chatId: string) {
    const snap = await getDoc(doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, chatId));
    return snap.exists() ? snap.data() : null;
  }

  // ── Search ───────────────────────────────────────────────────────────────────

  async searchChats(userId: string, searchTerm: string): Promise<ChatsType[]> {
    if (!this.isValidPrefixedId(userId)) return [];

    const q    = query(
      this.chatGroupsCollection,
      where('members', 'array-contains', userId),
      where('isActive', '==', true)
    );
    const snap = await getDocs(q);
    const results: ChatsType[] = [];

    for (const d of snap.docs) {
      const data    = d.data();
      const members: string[] = data.members || [];
      if (!members.includes(userId)) continue;
      if (!data.name?.toLowerCase().includes(searchTerm.toLowerCase())) continue;

      const clientId   = members.find((m) => m.startsWith('userid_'));
      const clientInfo = clientId ? await this.getUserInfo(clientId) : null;

      results.push({
        id:      d.id,
        name:    data.name || 'Unknown',
        status:  this.determineStatus(clientInfo?.onlineStatus),
        thumb:   data.photoURL || clientInfo?.profile_url || '/default-avatar.jpg',
        recent:  false,
        excerpt: data.lastMessage?.text || '',
        messages: [],
      });
    }

    return results;
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  private async buildAttachment(file: File) {
    const { url, fileName } = await this.uploadFileToBackend(file);
    const ext  = fileName.split('.').pop()?.toLowerCase() || '';
    let icon   = '/icons/file.svg';
    if (ext === 'pdf') icon = '/icons/pdf.svg';
    else if (['zip', 'rar'].includes(ext)) icon = '/icons/zip.svg';
    else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) icon = '/icons/image.svg';
    else if (['doc', 'docx'].includes(ext)) icon = '/icons/doc.svg';
    return { icon, file: fileName, fileSize: this.formatFileSize(file.size), url };
  }

  private formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k     = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i     = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  private async getUserInfo(userId: string) {
    try {
      const snap = await getDoc(doc(this.usersCollection, userId));
      return snap.exists() ? snap.data() : null;
    } catch {
      return null;
    }
  }

  private determineStatus(s: any): 'online' | 'offline' | 'away' | 'busy' {
    if (!s) return 'offline';
    if (typeof s === 'boolean') return s ? 'online' : 'offline';
    if (typeof s === 'string') {
      const v = s.toLowerCase();
      if (['online', 'offline', 'away', 'busy'].includes(v)) return v as any;
    }
    return 'offline';
  }

  // ── Create or update chat group after nutritionist assignment ────────────────
  //
  // Mirrors the Flutter flow:
  //   Step 1  get_user_info/{userId}/  → primary nutrist_id
  //   Step 2  get_user/{userId}/       → full name, photo, all nutritionist IDs
  //   Step 3  Firestore: create group doc if missing, else merge-update members
  //   Step 4  If newly created: send the onboarding welcome message
  //
  // groupId format: "userid_{userId}{nutristId}"  (matches Flutter/mobile)
  // Uses FIREBASE_COLLECTIONS so it automatically targets prod vs stage.

  async createOrUpdateChatGroup(userId: number): Promise<void> {
    try {
      // ── Step 1: resolve primary nutritionist ─────────────────────────────
      const userInfoData = await fetchUserInfo(userId);
      if (userInfoData?.success?.toString() !== '1') return;
      const nutristId = userInfoData.user_info?.nutrist_id;
      if (!nutristId) return;

      // ── Step 2: resolve user profile + full nutritionist list ────────────
      const userData = await getUser(userId);
      if (userData?.success?.toString() !== '1') return;
      const userObj = userData.user;

      const fullName = `${userObj?.user_fname || ''} ${userObj?.user_lname || ''}`.trim();
      const photoUrl = userObj?.profile_url || '';

      // members: the user + every assigned nutritionist + admin
      const members: string[] = [`userid_${userId}`];
      if (Array.isArray(userObj?.nutritionist_id)) {
        for (const id of userObj.nutritionist_id) {
          members.push(`nutritionistid_${id}`);
        }
      }
      members.push('adminid_2');

      const groupId = `userid_${userId}${nutristId}`;
      const nutriId = `nutritionistid_${nutristId}`;

      // ── Step 3: create or merge-update the Firestore group doc ───────────
      const groupRef  = doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, groupId);
      const groupSnap = await getDoc(groupRef);

      if (!groupSnap.exists()) {
        await setDoc(groupRef, {
          name:        fullName,
          photoURL:    photoUrl,
          createdAt:   serverTimestamp(),
          createdBy:   nutriId,
          members,
          memberCount: members.length,
          lastMessage: {
            text:      "Hi! I've been assigned as your personal coach - welcome aboard! I'm really excited to work with you and help you get started on your journey.",
            timestamp: serverTimestamp(),
            sender:    nutriId,
          },
          isActive: true,
        });

        // ── Step 4: send the onboarding welcome message ───────────────────
        await this.sendMessage(
          groupId,
          "Hello,\nWelcome on board! We're glad to have you with us.\nDo take some time to explore the app and the different plans available.\n\nIf you have any queries about navigating the app or need assistance getting started, you can drop a message here - I'll be happy to guide you. 😊",
          nutriId
        );
      } else {
        // Group already exists — update mutable fields only
        await setDoc(groupRef, {
          name:        fullName,
          photoURL:    photoUrl,
          members,
          memberCount: members.length,
          isActive:    true,
        }, { merge: true });
      }
    } catch (e) {
      console.error('Error creating/updating chat group:', e);
    }
  }
}

export const chatService = new ChatService();